
import axios from 'axios';
import { 
  Procedure, 
  ProcedureStatus, 
  TestPanel, 
  TestDefinition,
  ProcedureResult 
} from '../types/procedures';
import { getAuthToken } from '../utils/authUtils';

// Use environment variable or fallback to localhost for development
const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';

// Global cache for shared data (test panels, definitions, lab integrations)
interface CacheEntry<T> {
  data: T | null;
  timestamp: number;
  promise: Promise<T> | null;
}

const globalCache = {
  testPanels: {
    data: null as TestPanel[] | null,
    timestamp: 0,
    promise: null as Promise<TestPanel[]> | null
  } as CacheEntry<TestPanel[]>,
  testDefinitions: {
    data: null as TestDefinition[] | null,
    timestamp: 0,
    promise: null as Promise<TestDefinition[]> | null
  } as CacheEntry<TestDefinition[]>,
  labIntegrations: {
    data: null as { id: string, name: string }[] | null,
    timestamp: 0,
    promise: null as Promise<{ id: string, name: string }[]> | null
  } as CacheEntry<{ id: string, name: string }[]>
};

const CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes

/**
 * Secure Procedure Service
 * Uses backend API endpoints instead of direct Firestore access
 * Includes request deduplication and global caching to prevent repeated calls
 */
export class ProcedureService {
  private readonly proceduresEndpoint = `${API_BASE}/clinical/procedures`;
  private readonly testPanelsEndpoint = `${API_BASE}/clinical/test-panels`;
  private readonly testDefinitionsEndpoint = `${API_BASE}/clinical/test-definitions`;

  /**
   * Get cached data or fetch if expired/not cached
   * Uses request deduplication to prevent multiple simultaneous requests
   */
  private async getCachedData<T extends TestPanel[] | TestDefinition[] | { id: string, name: string }[]>(
    cacheKey: keyof typeof globalCache,
    fetchFunction: () => Promise<T>
  ): Promise<T> {
    const cache = globalCache[cacheKey] as CacheEntry<T>;
    const now = Date.now();

    // Return cached data if still valid
    if (cache.data && (now - cache.timestamp) < CACHE_EXPIRY) {
      console.log(`ProcedureService: Using cached ${cacheKey}`);
      return cache.data;
    }

    // If there's already a pending request, wait for it
    if (cache.promise) {
      console.log(`ProcedureService: Waiting for existing ${cacheKey} request`);
      return cache.promise;
    }

    // Create new request and cache the promise
    console.log(`ProcedureService: Fetching fresh ${cacheKey} data`);
    cache.promise = fetchFunction().then(data => {
      cache.data = data;
      cache.timestamp = now;
      cache.promise = null;
      return data;
    }).catch(error => {
      cache.promise = null;
      throw error;
    });

    return cache.promise;
  }

  /**
   * Clear global cache for a specific data type
   */
  static clearCache(cacheKey?: keyof typeof globalCache) {
    if (cacheKey) {
      globalCache[cacheKey].data = null;
      globalCache[cacheKey].timestamp = 0;
      globalCache[cacheKey].promise = null;
      console.log(`ProcedureService: Cleared ${cacheKey} cache`);
    } else {
      Object.keys(globalCache).forEach(key => {
        globalCache[key as keyof typeof globalCache].data = null;
        globalCache[key as keyof typeof globalCache].timestamp = 0;
        globalCache[key as keyof typeof globalCache].promise = null;
      });
      console.log('ProcedureService: Cleared all caches');
    }
  }

  /**
   * Get cache status for debugging
   */
  static getCacheStatus() {
    const now = Date.now();
    return {
      testPanels: {
        hasData: globalCache.testPanels.data !== null,
        age: globalCache.testPanels.data ? now - globalCache.testPanels.timestamp : null,
        hasPendingRequest: globalCache.testPanels.promise !== null
      },
      testDefinitions: {
        hasData: globalCache.testDefinitions.data !== null,
        age: globalCache.testDefinitions.data ? now - globalCache.testDefinitions.timestamp : null,
        hasPendingRequest: globalCache.testDefinitions.promise !== null
      },
      labIntegrations: {
        hasData: globalCache.labIntegrations.data !== null,
        age: globalCache.labIntegrations.data ? now - globalCache.labIntegrations.timestamp : null,
        hasPendingRequest: globalCache.labIntegrations.promise !== null
      }
    };
  }

  async createProcedure(procedure: Omit<Procedure, 'id' | 'createdAt' | 'updatedAt'>): Promise<Procedure> {
    try {
      console.log('Creating procedure via API:', procedure.testType);
      const token = await getAuthToken();
      
      const response = await axios.post(
        this.proceduresEndpoint,
        procedure,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return {
        id: response.data.id,
        ...response.data,
        status: ProcedureStatus.ORDERED
      };
    } catch (error) {
      console.error('Error creating procedure:', error);
      throw error;
    }
  }

  async updateProcedureStatus(
    procedureId: string, 
    status: ProcedureStatus, 
    updates: Partial<Procedure> = {}
  ): Promise<void> {
    try {
      console.log('Updating procedure status via API:', procedureId, status);
      const token = await getAuthToken();
      
      await axios.put(
        `${this.proceduresEndpoint}/${procedureId}/status`,
        {
          status,
          updates
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
    } catch (error) {
      console.error('Error updating procedure status:', error);
      throw error;
    }
  }

  async addProcedureResult(
    procedureId: string, 
    result: Omit<ProcedureResult, 'id'>
  ): Promise<void> {
    try {
      console.log('Adding procedure result via API:', procedureId);
      const token = await getAuthToken();
      
      await axios.post(
        `${this.proceduresEndpoint}/${procedureId}/results`,
        { result },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
    } catch (error) {
      console.error('Error adding procedure result:', error);
      throw error;
    }
  }

  async getPatientProcedures(patientId?: string): Promise<Procedure[]> {
    try {
      console.log('Getting patient procedures via API:', patientId);
      const token = await getAuthToken();
      
      if (!patientId) {
        throw new Error('Patient ID is required');
      }
      
      const response = await axios.get(
        `${API_BASE}/clinical/patients/${patientId}/procedures`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.procedures.map((proc: any) => ({ 
        id: proc.id, 
        ...proc 
      })) as Procedure[];
    } catch (error) {
      console.error('Error getting patient procedures:', error);
      throw error;
    }
  }

  async getAllProcedures(facilityId: string): Promise<Procedure[]> {
    try {
      console.log('Getting all procedures via API:', facilityId);
      const token = await getAuthToken();
      
      const params = new URLSearchParams();
      params.append('facilityId', facilityId);
      
      const response = await axios.get(
        `${this.proceduresEndpoint}?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.procedures.map((proc: any) => ({ 
        id: proc.id, 
        ...proc 
      })) as Procedure[];
    } catch (error) {
      console.error('Error getting all procedures:', error);
      throw error;
    }
  }

  async getProcedureById(procedureId: string): Promise<Procedure | null> {
    try {
      console.log('Getting procedure by ID via API:', procedureId);
      const token = await getAuthToken();
      
      const response = await axios.get(
        `${this.proceduresEndpoint}/${procedureId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return {
        id: response.data.procedure.id,
        ...response.data.procedure
      } as Procedure;
    } catch (error) {
      console.error('Error getting procedure by ID:', error);
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async createTestPanel(panel: Omit<TestPanel, 'id' | 'createdAt' | 'updatedAt'>): Promise<TestPanel> {
    try {
      console.log('Creating test panel via API:', panel.name);
      const token = await getAuthToken();
      
      const response = await axios.post(
        this.testPanelsEndpoint,
        panel,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Clear test panels cache since we added a new panel
      ProcedureService.clearCache('testPanels');
      
      return {
        id: response.data.id,
        ...response.data
      };
    } catch (error) {
      console.error('Error creating test panel:', error);
      throw error;
    }
  }

  async updateTestPanel(id: string, panel: Omit<TestPanel, 'id' | 'createdAt' | 'updatedAt'>): Promise<TestPanel> {
    try {
      console.log('Updating test panel via API:', id);
      const token = await getAuthToken();
      
      await axios.put(
        `${this.testPanelsEndpoint}/${id}`,
        panel,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Clear test panels cache since we updated a panel
      ProcedureService.clearCache('testPanels');
      
      return { 
        id, 
        ...panel, 
        updatedAt: new Date() 
      } as TestPanel;
    } catch (error) {
      console.error('Error updating test panel:', error);
      throw error;
    }
  }

  async getTestPanels(): Promise<TestPanel[]> {
    return this.getCachedData('testPanels', async () => {
      console.log('Getting test panels via API');
      const token = await getAuthToken();
      
      const response = await axios.get(
        this.testPanelsEndpoint,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.testPanels.map((panel: any) => ({ 
        id: panel.id, 
        ...panel 
      })) as TestPanel[];
    });
  }

  // Supports all fields in TestDefinition, including new ones from the test definition form
  async createTestDefinition(testData: Omit<TestDefinition, 'id'>): Promise<TestDefinition> {
    try {
      console.log('Creating test definition via API:', testData.name);
      const token = await getAuthToken();
      
      const response = await axios.post(
        this.testDefinitionsEndpoint,
        testData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Clear test definitions cache since we added a new definition
      ProcedureService.clearCache('testDefinitions');
      
      return { 
        id: response.data.id, 
        ...response.data 
      } as TestDefinition;
    } catch (error) {
      console.error('Error creating test definition:', error);
      throw error;
    }
  }

  async updateTestDefinition(id: string, testData: Omit<TestDefinition, 'id'>): Promise<TestDefinition> {
    try {
      console.log('Updating test definition via API:', id);
      const token = await getAuthToken();
      
      await axios.put(
        `${this.testDefinitionsEndpoint}/${id}`,
        testData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Clear test definitions cache since we updated a definition
      ProcedureService.clearCache('testDefinitions');
      
      return { 
        id, 
        ...testData 
      } as TestDefinition;
    } catch (error) {
      console.error('Error updating test definition:', error);
      throw error;
    }
  }

  async getTestDefinitions(): Promise<TestDefinition[]> {
    return this.getCachedData('testDefinitions', async () => {
      console.log('Getting test definitions via API');
      const token = await getAuthToken();
      
      const response = await axios.get(
        this.testDefinitionsEndpoint,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.testDefinitions.map((def: any) => ({ 
        id: def.id, 
        ...def 
      })) as TestDefinition[];
    });
  }

  async getTestDefinitionsByOrderType(orderType: string): Promise<TestDefinition[]> {
    try {
      console.log('Getting test definitions by order type via API:', orderType);
      const token = await getAuthToken();
      
      const params = new URLSearchParams();
      params.append('orderType', orderType);
      
      const response = await axios.get(
        `${this.testDefinitionsEndpoint}?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.testDefinitions.map((def: any) => ({ 
        id: def.id, 
        ...def 
      })) as TestDefinition[];
    } catch (error) {
      console.error('Error getting test definitions by order type:', error);
      throw error;
    }
  }

  async getLabIntegrations(facilityId?: string): Promise<{ id: string, name: string, assignedFacilities?: string[] }[]> {
    // Always use 'labIntegrations' as the cache key and filter client-side if facilityId is provided
    const allIntegrations = await this.getCachedData('labIntegrations', async () => {
      console.log('Getting lab integrations via API');
      const token = await getAuthToken();
      
      const params = new URLSearchParams();
      params.append('active', 'true'); // Only show active integrations
      
      const url = `${API_BASE}/integrations/lab-integrations?${params.toString()}`;
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      return response.data.labIntegrations.map((integration: any) => ({ 
        id: integration.id, 
        name: integration.name,
        assignedFacilities: integration.assignedFacilities
      }));
    });
    
    // Filter by facilityId client-side if provided
    if (facilityId) {
      return allIntegrations.filter((integration: { id: string, name: string, assignedFacilities?: string[] }) => 
        !integration.assignedFacilities || 
        integration.assignedFacilities.length === 0 || 
        integration.assignedFacilities.includes(facilityId)
      );
    }
    
    return allIntegrations;
  }

  // Additional convenience methods for procedure management

  async getProcedures(filters: Record<string, any> = {}, page: number = 1, limit: number = 50): Promise<{ procedures: Procedure[], pagination: any }> {
    try {
      console.log('Getting procedures with filters via API:', filters);
      const token = await getAuthToken();
      
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      
      // Add filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
      
      const response = await axios.get(
        `${this.proceduresEndpoint}?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return {
        procedures: response.data.procedures,
        pagination: response.data.pagination
      };
    } catch (error) {
      console.error('Error getting procedures:', error);
      throw error;
    }
  }

  async getProceduresByTestType(testType: string, options: Record<string, any> = {}): Promise<Procedure[]> {
    try {
      console.log('Getting procedures by test type via API:', testType);
      const token = await getAuthToken();
      
      const params = new URLSearchParams();
      params.append('testType', testType);
      
      // Add additional filters
      if (options.facilityId) params.append('facilityId', options.facilityId);
      if (options.status) params.append('status', options.status);
      if (options.page) params.append('page', options.page.toString());
      if (options.limit) params.append('limit', options.limit.toString());
      
      const response = await axios.get(
        `${this.proceduresEndpoint}/by-test-type?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.procedures || [];
    } catch (error) {
      console.error('Error getting procedures by test type:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  }

  async getProceduresByStatus(status: ProcedureStatus, facilityId?: string): Promise<Procedure[]> {
    try {
      const filters: Record<string, any> = { status };
      if (facilityId) {
        filters.facilityId = facilityId;
      }
      
      const result = await this.getProcedures(filters, 1, 1000);
      return result.procedures;
    } catch (error) {
      console.error('Error getting procedures by status:', error);
      throw error;
    }
  }

  async getProceduresByFacility(facilityId: string, page: number = 1, limit: number = 50): Promise<{ procedures: Procedure[], pagination: any }> {
    try {
      return await this.getProcedures({ facilityId }, page, limit);
    } catch (error) {
      console.error('Error getting procedures by facility:', error);
      throw error;
    }
  }

  async searchProcedures(searchTerm: string, facilityId?: string): Promise<Procedure[]> {
    try {
      const filters: Record<string, any> = { search: searchTerm };
      if (facilityId) {
        filters.facilityId = facilityId;
      }
      
      const result = await this.getProcedures(filters, 1, 100);
      return result.procedures;
    } catch (error) {
      console.error('Error searching procedures:', error);
      throw error;
    }
  }

  async cancelProcedure(procedureId: string, reason?: string): Promise<void> {
    try {
      await this.updateProcedureStatus(procedureId, ProcedureStatus.CANCELLED, {
        cancellationReason: reason
      } as Partial<Procedure>);
    } catch (error) {
      console.error('Error cancelling procedure:', error);
      throw error;
    }
  }

  async completeProcedure(procedureId: string, result: Omit<ProcedureResult, 'id'>): Promise<void> {
    try {
      await this.addProcedureResult(procedureId, result);
    } catch (error) {
      console.error('Error completing procedure:', error);
      throw error;
    }
  }

  async getProcedureStatistics(facilityId?: string): Promise<Record<string, number>> {
    try {
      const filters: Record<string, any> = {};
      if (facilityId) {
        filters.facilityId = facilityId;
      }
      
      const result = await this.getProcedures(filters, 1, 1000);
      const procedures = result.procedures;
      
      const stats: Record<string, number> = {
        total: procedures.length,
        ordered: 0,
        inProgress: 0,
        completed: 0,
        cancelled: 0
      };
      
      procedures.forEach(procedure => {
        switch (procedure.status) {
          case ProcedureStatus.ORDERED:
            stats.ordered++;
            break;
          case ProcedureStatus.PENDING:
            stats.inProgress++;
            break;
          case ProcedureStatus.COMPLETED:
            stats.completed++;
            break;
          case ProcedureStatus.CANCELLED:
            stats.cancelled++;
            break;
        }
      });
      
      return stats;
    } catch (error) {
      console.error('Error getting procedure statistics:', error);
      throw error;
    }
  }
}
